// Фото витрины: снимаем камерой, сжимаем, сразу отправляем. Без сети снимок
// ждёт в IndexedDB и уходит перед закрытием точки.

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { prepareImage } from '@/features/chat/lib/image-compress';
import { apiErrorMessage, isNetworkError } from '@/lib/api';
import { deletePhoto, getPhoto, putPhoto } from '@/lib/idb';
import { uploadPointPhoto } from '@/services/courier';

const SHELF_PHOTO = { maxSide: 1600, quality: 0.8, maxBytes: 600_000 };

export type PhotoReady = 'uploaded' | 'pending' | 'missing';

export function useVisitPhoto(shiftId: string, pointId: string, serverUrl: string | null) {
  const qc = useQueryClient();
  const key = `${shiftId}:${pointId}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploaded, setUploaded] = useState<string | null>(serverUrl);
  const [local, setLocal] = useState<{ url: string; blob: Blob; name: string } | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [preparing, setPreparing] = useState(false);

  // Снимок, не ушедший в прошлый раз (закрыли приложение без сети).
  useEffect(() => {
    let alive = true;
    void getPhoto(key)
      .then((p) => {
        if (alive && p) setLocal({ url: URL.createObjectURL(p.blob), blob: p.blob, name: p.name });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [key]);

  useEffect(
    () => () => {
      if (local) URL.revokeObjectURL(local.url);
    },
    [local],
  );

  const upload = async (blob: Blob, name: string): Promise<'ok' | 'offline' | 'rejected'> => {
    setProgress(0);
    try {
      const res = await uploadPointPhoto(pointId, blob, name, setProgress);
      await deletePhoto(key).catch(() => undefined);
      setUploaded(res.photoUrl);
      setLocal(null);
      void qc.invalidateQueries({ queryKey: ['point', pointId] });
      return 'ok';
    } catch (err) {
      if (isNetworkError(err)) return 'offline';
      await deletePhoto(key).catch(() => undefined);
      setLocal(null);
      toast.error(apiErrorMessage(err, 'Фото не отправилось — снимите ещё раз'), { duration: 6000 });
      return 'rejected';
    } finally {
      setProgress(null);
    }
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setPreparing(true);
    let prepared: { blob: Blob; name: string };
    try {
      prepared = await prepareImage(file, SHELF_PHOTO);
    } catch {
      prepared = { blob: file, name: file.name || 'shelf.jpg' };
    } finally {
      setPreparing(false);
    }
    // Сначала в телефон: приложение закроют на середине отправки — снимок не пропадёт.
    await putPhoto(key, prepared).catch(() => undefined);
    setLocal({ url: URL.createObjectURL(prepared.blob), ...prepared });
    if (navigator.onLine) await upload(prepared.blob, prepared.name);
  };

  /** Перед закрытием точки: фото должно быть на сервере или ждать в очереди. */
  const ensureReady = async (): Promise<PhotoReady> => {
    if (local) {
      const r = await upload(local.blob, local.name);
      if (r === 'ok') return 'uploaded';
      if (r === 'offline') return 'pending';
      return 'missing';
    }
    return uploaded ? 'uploaded' : 'missing';
  };

  return {
    inputRef,
    previewUrl: local?.url ?? uploaded,
    /** Снимок есть в телефоне, но ещё не на сервере. */
    pendingUpload: !!local && progress == null,
    progress,
    preparing,
    has: !!local || !!uploaded,
    openCamera: () => inputRef.current?.click(),
    onFile,
    ensureReady,
  };
}

export type VisitPhoto = ReturnType<typeof useVisitPhoto>;
